import type { Product } from '../types';
import { shouldTrackAnalytics } from './siteAnalytics';

type Ga4Item = {
  item_id: string;
  item_name: string;
  item_category?: string;
  price: number;
  quantity: number;
  index?: number;
};

type Ga4ItemInput = Pick<Product, 'id' | 'name' | 'category' | 'pricePerDay' | 'priceOnRequest' | 'slug'>;

export function productToGa4Item(product: Ga4ItemInput, index?: number): Ga4Item {
  const item: Ga4Item = {
    item_id: product.id,
    item_name: product.name,
    item_category: product.category || undefined,
    price: product.priceOnRequest ? 0 : Number(product.pricePerDay) || 0,
    quantity: 1,
  };

  if (index != null) {
    item.index = index;
  }

  return item;
}

export function ga4ItemListValue(items: Ga4Item[]): number {
  return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
}

export function pushToDataLayer(payload: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  if (!shouldTrackAnalytics()) return;

  try {
    const w = window as Window & { dataLayer?: unknown[] };
    w.dataLayer = Array.isArray(w.dataLayer) ? w.dataLayer : [];
    if (import.meta.env.DEV) {
      console.log('[tracking] dataLayer.push', payload);
    }
    w.dataLayer.push(payload);
  } catch (e) {
    console.warn('[tracking] dataLayer.push failed', e);
  }
}

/** GA4 view_item – produktsida. Fungerar automatiskt för alla produkter i DB. */
export function trackViewItem(product: Ga4ItemInput): void {
  const item = productToGa4Item(product);

  pushToDataLayer({
    event: 'view_item',
    currency: 'SEK',
    value: item.price,
    items: [item],
  });
}

/** GA4 view_item_list – produktlistan /produkter. */
export function trackViewItemList(
  products: Ga4ItemInput[],
  listName = 'Produkter'
): void {
  if (products.length === 0) return;

  const items = products.map((product, index) => productToGa4Item(product, index));

  pushToDataLayer({
    event: 'view_item_list',
    item_list_name: listName,
    item_list_id: listName.toLowerCase().replace(/\s+/g, '_'),
    currency: 'SEK',
    value: ga4ItemListValue(items),
    items,
  });
}

/** GA4 select_item – klick på produkt i lista/karusell. */
export function trackSelectItem(product: Ga4ItemInput, listName = 'Produkter'): void {
  const item = productToGa4Item(product);

  pushToDataLayer({
    event: 'select_item',
    item_list_name: listName,
    item_list_id: listName.toLowerCase().replace(/\s+/g, '_'),
    currency: 'SEK',
    items: [item],
  });
}
